import { AlertTriangle, CheckCircle2, Clock, Play } from 'lucide-react';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import { useRunTask, useWorkerStatus } from '@/hooks/worker';
import { ApiError } from '@/lib/api';
import { formatInstant } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import {
  TASK_DESCRIPTIONS,
  TASK_LABELS,
  TASK_PENDING_PHASE,
  TASK_SCHEDULES,
  type TaskName,
  type TaskRunView,
  type TaskSummary,
} from '@shared/schemas/worker';

/*
 * /admin/worker — ROADMAP.md §7.6's dashboard.
 *
 * **Every task the business will have is listed, including the ones not built
 * yet.** A dashboard that only shows what happens to exist cannot tell an admin
 * that job reminders are not running — it simply does not mention them, which
 * reads as fine. The ones waiting on a later phase say so by name.
 *
 * **"Run now" goes through the same ledger the schedule does**, so it is safe to
 * press twice — and when the period is already claimed the screen says "already
 * run" rather than pretending. That honesty is the point: an office that has
 * read it once will press the button when they are not sure whether last night
 * happened, which is exactly when they should.
 */

const STATUS_TONES: Record<TaskRunView['status'], StatusTone> = {
  ok: 'completed',
  error: 'priority',
  running: 'in-progress',
};

export function WorkerPage() {
  const status = useWorkerStatus();

  return (
    <AdminPage
      title="Background Tasks"
      description="What runs on its own, when it last ran, and whether it worked."
    >
      {status.isError ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="The task history could not be loaded"
            onRetry={() => {
              void status.refetch();
            }}
          />
        </div>
      ) : status.isPending ? (
        <div aria-busy="true">
          <span className="sr-only">Loading background tasks</span>
          <div aria-hidden="true" className="h-96 animate-pulse rounded-lg bg-white" />
        </div>
      ) : (
        <>
          {/*
            The banner appears only when something failed. A permanent "all
            good" strip is a strip people stop reading, and then the day it
            says something else they do not notice.
          */}
          {status.data.recentFailures > 0 ? (
            <p
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-sm border border-status-priority-text/30 bg-red-50 px-3 py-2.5 text-body text-status-priority-text"
            >
              <AlertTriangle aria-hidden="true" className="mt-0.5 size-[18px] shrink-0" />
              {status.data.recentFailures} task run
              {status.data.recentFailures === 1 ? '' : 's'} failed in the last day. The detail is on
              the task below.
            </p>
          ) : null}

          <ul className="space-y-3">
            {status.data.tasks.map((task) => (
              <TaskCard key={task.taskName} task={task} />
            ))}
          </ul>
        </>
      )}
    </AdminPage>
  );
}

function TaskCard({ task }: { task: TaskSummary }) {
  const toast = useToast();
  const run = useRunTask();

  const name = task.taskName as TaskName;
  const pending = TASK_PENDING_PHASE[name];
  const last = task.lastRun;

  return (
    <li className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex flex-wrap items-center gap-2 text-h3 text-ink-900">
            {/* The display value. `stale-clock-in-alert` never reaches a screen (P5). */}
            {TASK_LABELS[name] ?? task.taskName}
            {last === null ? null : (
              <StatusBadge
                tone={last.isStuck ? 'priority' : STATUS_TONES[last.status]}
                label={
                  last.isStuck
                    ? 'Stuck'
                    : last.status === 'ok'
                      ? 'Worked'
                      : last.status === 'error'
                        ? 'Failed'
                        : 'Running'
                }
              />
            )}
            {pending === undefined ? null : (
              <span className="rounded-full bg-ink-100 px-2.5 py-0.5 text-meta text-ink-700">
                {pending}
              </span>
            )}
          </h2>
          <p className="mt-1 text-body text-ink-500">{TASK_DESCRIPTIONS[name] ?? ''}</p>
          <p className="mt-0.5 flex items-center gap-1.5 text-meta text-ink-500">
            <Clock aria-hidden="true" className="size-3.5" />
            {TASK_SCHEDULES[name] ?? 'On a schedule'}
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          disabled={run.isPending}
          onClick={() => {
            run.mutate(name, {
              onSuccess: (outcome) => {
                toast.success(
                  outcome.ran
                    ? `${TASK_LABELS[name] ?? task.taskName} ran.`
                    : // The honest answer. The run key for this period is
                      // already claimed, which is the ledger working.
                      `Already run for this period — nothing to do.`,
                );
              },
              onError: (error) => {
                toast.error(
                  error instanceof ApiError ? error.message : 'That task could not be run.',
                );
              },
            });
          }}
        >
          <Play aria-hidden="true" />
          Run now
        </Button>
      </div>

      {last === null ? (
        <p className="mt-3 text-meta text-ink-500">
          {/* Never blank — "has not run" is a fact worth stating (P1, P10). */}
          Has not run yet.
        </p>
      ) : (
        <div className="mt-3 border-t border-ink-100 pt-3">
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-700">
            <span className="flex items-center gap-1.5">
              {last.status === 'ok' && !last.isStuck ? (
                <CheckCircle2 aria-hidden="true" className="size-3.5 text-status-completed-text" />
              ) : (
                <AlertTriangle aria-hidden="true" className="size-3.5 text-status-priority-text" />
              )}
              {formatInstant(last.startedAt)}
            </span>
            {last.durationMs === null ? (
              <span className="text-ink-500">still running</span>
            ) : (
              <span className="text-ink-500">
                took{' '}
                {last.durationMs < 1000
                  ? `${String(last.durationMs)}ms`
                  : `${(last.durationMs / 1000).toFixed(1)}s`}
              </span>
            )}
            {/* The claim itself, so "did tonight's run happen" is answerable. */}
            <span className="text-ink-500">{last.runKey}</span>
          </p>

          {last.isStuck ? (
            <p className="mt-1.5 text-meta text-status-priority-text">
              This run never finished — the process probably restarted mid-task. It will not start
              again on its own until the period rolls over; use Run now once that happens.
            </p>
          ) : null}

          {last.detail === null || last.detail === undefined ? null : (
            // The counts the task reported. "0 created, 14 skipped" is the
            // idempotency working, and it is worth being able to see it.
            <pre className="bg-paper-50 mt-2 overflow-x-auto rounded-sm p-2 text-meta text-ink-700">
              {JSON.stringify(last.detail, null, 2)}
            </pre>
          )}

          {task.recentRuns.length > 1 ? (
            <details className="mt-2">
              <summary className="inline-flex min-h-touch cursor-pointer items-center text-meta text-bark-700">
                Earlier runs
              </summary>
              <ul className="mt-1 space-y-1">
                {task.recentRuns.slice(1).map((entry) => (
                  <li key={entry.id} className="text-meta text-ink-500">
                    {formatInstant(entry.startedAt)} — {entry.status}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      )}
    </li>
  );
}
